import {pullRequestChangesPath, pullRequestChangesCommitRangePath} from '@github-ui/paths'
import {agoString} from '@github-ui/ago'
import {CommitSelector, type CommitItemProps, type CommitSelectorHandle} from '@github-ui/commit-selector'
import {TriangleDownIcon, VersionsIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useRef, useState} from 'react'
import type {FileTreePayload} from '../../page-data/payloads/file-tree'
import {useComparisonInfo, useHasCommitRange, useSelectedRange} from '../../stores/selectors'
import {useCommitSelectorLabel} from '../../hooks/use-commit-selector-label'
import {shortSha} from '@github-ui/commits/utils/short-sha'
import {announce} from '@github-ui/aria-live'
import {GlobalCommands} from '@github-ui/ui-commands'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type CommitSelection =
  | {type: 'range'; baseOid: string; endOid: string; fromPRBase: boolean} // Exclusive of base, inclusive of end
  | {type: 'single'; oid: string} // One commit only
  | {type: 'unfiltered'} // Everything

export interface ChangesSelectorProps extends Pick<
  FileTreePayload,
  'ownerLogin' | 'repositoryName' | 'pullRequestNumber'
> {
  lastReviewOid?: string | null
  commits: Array<{
    oid: string
    shortOid: string
    messageHeadline: string
    actorLogin: string
    createdAt: string
  }>
  variant?: 'default' | 'condensed'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ChangesSelector({
  ownerLogin,
  repositoryName,
  pullRequestNumber,
  commits,
  lastReviewOid,
  variant = 'default',
  open: controlledOpen,
  onOpenChange,
}: ChangesSelectorProps) {
  const {baseOid: startOid, headOid: endOid} = useSelectedRange()
  const hasCommitRange = useHasCommitRange()
  const {
    fullDiff: {baseOid: baseRefOid},
  } = useComparisonInfo()
  const verboseCommitSelectorAnnouncements = isFeatureEnabled('verbose_commit_selector_announcements')

  const commitsSelectorRef = useRef<CommitSelectorHandle>(null)
  const [internalOpen, setInternalOpen] = useState(false)

  // Determines if `open` is passed as a prop to ChangesSelector,
  // or if it is being controlled internally
  const isControlled = controlledOpen !== undefined
  const commitSelectorOpen = isControlled ? controlledOpen : internalOpen

  const setCommitSelectorOpen = useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    },
    [isControlled, onOpenChange],
  )

  // State to track range selection (similar to CommitsSelector)
  const commitOids = useMemo(() => commits.map(commit => commit.oid), [commits])

  // Calculate button text using shared hook
  const buttonText = useCommitSelectorLabel(commitOids)

  // Calculate initial range based on current selection
  const initialSelectedRange = useMemo(() => {
    if (!startOid || !endOid) return undefined

    // start commit oid is the base of the comparison (exclusive),
    // so we need to add 1 to the index to get the first commit that's actually selected
    const startCommitIndex = startOid ? commitOids.indexOf(startOid) + 1 : -1
    const endCommitIndex = endOid ? commitOids.indexOf(endOid) : -1

    if (startCommitIndex < 0 || endCommitIndex < 0) return undefined

    return {start: startCommitIndex, end: endCommitIndex}
  }, [startOid, endOid, commitOids])

  const [selectedRange, setSelectedRange] = useState<{start: number; end: number} | undefined>(
    () => initialSelectedRange,
  )

  // Get selected commit IDs based on range (similar to CommitsSelector)
  const selectedCommitIds = useMemo(() => {
    if (!selectedRange) return new Set<string>()
    return new Set(commitOids.slice(selectedRange.start, selectedRange.end + 1))
  }, [selectedRange, commitOids])

  // Transform commits data for CommitSelector
  const commitItems = useMemo(() => {
    const items: CommitItemProps[] = []

    // Add "All commits" option as a single selection item
    items.push({
      id: 'all-commits',
      label: 'All commits',
      description: `${commits.length} commit${commits.length !== 1 ? 's' : ''}`,
      descriptionVariant: 'block',
      selectionVariant: 'single',
      selected: !hasCommitRange,
    })

    // Add "Changes since last review" option - always show but disable when not applicable
    const lastCommitOid = commits[commits.length - 1]?.oid
    let changesSinceReviewDescription: string
    let changesSinceReviewDisabled = false
    let changesSinceLastReviewSelected = false

    if (!lastReviewOid) {
      changesSinceReviewDescription = 'No previous review found'
      changesSinceReviewDisabled = true
    } else if (!lastCommitOid) {
      changesSinceReviewDescription = 'No commits available'
      changesSinceReviewDisabled = true
    } else if (lastReviewOid === lastCommitOid) {
      changesSinceReviewDescription = 'No new commits since last review'
      changesSinceReviewDisabled = true
    } else {
      // Calculate commits since last review
      const lastReviewIndex = commitOids.indexOf(lastReviewOid)
      const commitsSinceReview = lastReviewIndex >= 0 ? commitOids.length - lastReviewIndex - 1 : commits.length
      changesSinceReviewDescription = `${commitsSinceReview} commit${commitsSinceReview !== 1 ? 's' : ''}`
      changesSinceLastReviewSelected = startOid === lastReviewOid && endOid === lastCommitOid
    }

    items.push({
      id: 'changes-since-review',
      label: 'Changes since your last review',
      description: changesSinceReviewDescription,
      descriptionVariant: 'block',
      selectionVariant: 'single',
      selected: changesSinceLastReviewSelected,
      disabled: changesSinceReviewDisabled,
    })

    // Add individual commits as multiple selection items with range selection
    for (const [index, commit] of commits.entries()) {
      items.push({
        id: commit.oid,
        label: commit.messageHeadline,
        description: `${commit.actorLogin} committed ${agoString(new Date(commit.createdAt))}`,
        descriptionVariant: 'block',
        selectionVariant: 'multiple',
        selected: selectedCommitIds.has(commit.oid),
        groupId: 1,
        groupTitle: index === 0 ? 'Select a range of commits' : undefined,
        trailingVisual: commit.shortOid,
      })
    }

    return items
  }, [commits, hasCommitRange, lastReviewOid, startOid, endOid, selectedCommitIds, commitOids])

  const navigateToCommitSelection = useCallback(
    (selection: CommitSelection) => {
      let path: string

      // Use changes routes
      if (selection.type === 'unfiltered') {
        path = pullRequestChangesPath({owner: ownerLogin, repo: repositoryName, number: pullRequestNumber})
      } else if (selection.type === 'range') {
        // Use "BASE" when the range's base is the PR's base commit
        const base = selection.fromPRBase ? 'BASE' : selection.baseOid

        path = pullRequestChangesCommitRangePath({
          owner: ownerLogin,
          repo: repositoryName,
          number: pullRequestNumber,
          base,
          head: selection.endOid,
        })
      } else {
        // selection.type === 'single'
        path = pullRequestChangesCommitRangePath({
          owner: ownerLogin,
          repo: repositoryName,
          number: pullRequestNumber,
          head: selection.oid,
        })
      }

      window.location.href = path
    },
    [ownerLogin, repositoryName, pullRequestNumber],
  )

  const handleSingleSelect = (selectedItem: {id: string}) => {
    // Handle single selection behavior - immediate navigation
    if (selectedItem.id === 'all-commits') {
      navigateToCommitSelection({type: 'unfiltered'})
    } else if (selectedItem.id === 'changes-since-review' && lastReviewOid) {
      const lastCommitOid = commits[commits.length - 1]?.oid
      if (lastCommitOid) {
        navigateToCommitSelection({
          type: 'range',
          fromPRBase: lastReviewOid === baseRefOid,
          baseOid: lastReviewOid,
          endOid: lastCommitOid,
        })
      }
    }
  }

  // Range selection logic from CommitsSelector
  const getUpdatedSelectedRange = useCallback(
    (
      changedIndex: number,
      firstSelectedIndex: number,
      lastSelectedIndex: number,
      changeType: 'added' | 'removed',
    ): {newStartIndex: number; newEndIndex: number} => {
      let newStartIndex: number
      let newEndIndex: number

      if (changeType === 'added') {
        newStartIndex = Math.min(changedIndex, firstSelectedIndex)
        newEndIndex = Math.max(changedIndex, lastSelectedIndex)
      } else {
        // when removing commits, we prioritize removing from the end of the range
        if (changedIndex === firstSelectedIndex) {
          // just remove the first item if that's what was selected
          newStartIndex = firstSelectedIndex + 1
          newEndIndex = lastSelectedIndex
        } else {
          // otherwise just remove the item and everything after it
          newStartIndex = firstSelectedIndex
          newEndIndex = changedIndex - 1
        }
      }

      return {newStartIndex, newEndIndex}
    },
    [],
  )

  const handleMultiSelect = useCallback(
    (selectedItem: {id: string}) => {
      // Handle multi-select behavior with range selection (similar to CommitsSelector)
      const itemIndex = commitOids.indexOf(selectedItem.id)
      const isCurrentlySelected = selectedCommitIds.has(selectedItem.id)
      const changeType: 'added' | 'removed' = isCurrentlySelected ? 'removed' : 'added'

      let newRange: {start: number; end: number} | undefined

      if (!selectedRange) {
        // if we don't have a selected range, just select the item or return nothing
        newRange = changeType === 'added' ? {start: itemIndex, end: itemIndex} : undefined
      } else {
        const lastItemRemoved = selectedRange.start === selectedRange.end && changeType === 'removed'
        if (!lastItemRemoved) {
          const {newStartIndex, newEndIndex} = getUpdatedSelectedRange(
            itemIndex,
            selectedRange.start,
            selectedRange.end,
            changeType,
          )
          newRange = {start: newStartIndex, end: newEndIndex}
        }
      }

      if (!newRange) {
        announce('No commits selected')
      } else if (newRange.start !== undefined && newRange.start === newRange.end) {
        const position = newRange.start + 1
        const commitOid = commitOids[newRange.start]

        if (commitOid) {
          const sha = shortSha(commitOid)
          const message = verboseCommitSelectorAnnouncements ? `Commit ${sha} selected` : `Commit ${position} selected`
          announce(message)
        }
      } else if (newRange.start !== undefined && newRange.end !== undefined) {
        const startPosition = newRange.start + 1
        const endPosition = newRange.end + 1
        const startCommitOid = commitOids[newRange.start]
        const endCommitOid = commitOids[newRange.end]

        if (startCommitOid && endCommitOid) {
          const startSha = shortSha(startCommitOid)
          const endSha = shortSha(endCommitOid)
          const message = verboseCommitSelectorAnnouncements
            ? `Commits ${startSha} to ${endSha} selected`
            : `Commits ${startPosition} to ${endPosition} selected`
          announce(message)
        }
      }

      setSelectedRange(newRange)
    },
    [
      commitOids,
      selectedCommitIds,
      selectedRange,
      getUpdatedSelectedRange,
      setSelectedRange,
      verboseCommitSelectorAnnouncements,
    ],
  )

  const handleApplySelection = useCallback(() => {
    // Use our controlled selectedRange state instead of relying on selectedItems parameter
    if (
      !selectedRange ||
      (selectedRange.start === initialSelectedRange?.start && selectedRange.end === initialSelectedRange?.end)
    )
      return

    // Calculate start and end commit OIDs based on the selected range
    // The base commit of the diff we show will be one earlier commit than the selected start commit
    // since this commit will be the base of the comparison and not included in the diff.
    // If the selected start commit is the first commit of the PR, we use the base ref as the start.
    let startCommitOid: string | undefined
    let endCommitOid: string | undefined

    // If the selected range is not the full range, we need to set start and end OIDs
    // If the selected range is the full range, we navigate to unfiltered view
    // For single commit PRs, we navigate to the commit even though it's the full range
    if (selectedRange.start > 0 || selectedRange.end < commitOids.length - 1 || commitOids.length === 1) {
      startCommitOid = selectedRange.start > 0 ? commitOids[selectedRange.start - 1] : baseRefOid
      endCommitOid = commitOids[selectedRange.end]
    }

    if (!startCommitOid || !endCommitOid) {
      navigateToCommitSelection({type: 'unfiltered'})
    } else if (selectedRange.start === selectedRange.end) {
      // Single commit selection
      navigateToCommitSelection({type: 'single', oid: endCommitOid})
    } else {
      // Multiple commits - use range from base to end
      navigateToCommitSelection({
        type: 'range',
        fromPRBase: startCommitOid === baseRefOid,
        baseOid: startCommitOid,
        endOid: endCommitOid,
      })
    }
  }, [
    selectedRange,
    initialSelectedRange?.start,
    initialSelectedRange?.end,
    commitOids,
    baseRefOid,
    navigateToCommitSelection,
  ])

  const handleCancelSelection = useCallback(() => {
    // Reset the selected range to initial value, restoring the original selection
    setSelectedRange(initialSelectedRange)
  }, [setSelectedRange, initialSelectedRange])

  return (
    <>
      <GlobalCommands
        commands={{
          'pull-request-files-changed:open-commits-list': () => {
            setCommitSelectorOpen(true)
          },
        }}
      />
      <CommitSelector
        buttonProps={{
          id: 'changes-selector-button',
          size: 'small',
          leadingVisual: variant === 'condensed' ? undefined : VersionsIcon,
          variant: variant === 'condensed' ? 'invisible' : 'default',
          trailingAction: TriangleDownIcon,
        }}
        anchorLabel={buttonText}
        ref={commitsSelectorRef}
        dialogProps={{
          height: 'large',
        }}
        items={commitItems}
        onSingleSelect={handleSingleSelect}
        onMultiSelect={handleMultiSelect}
        onApply={handleApplySelection}
        onCancel={handleCancelSelection}
        variant="modal"
        title="Select commits to view"
        open={commitSelectorOpen}
        onOpenChange={setCommitSelectorOpen}
      />
    </>
  )
}

try{ ChangesSelector.displayName ||= 'ChangesSelector' } catch {}